function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>'"]/g, char => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;"
  })[char]);
}

function authFrame(content) {
  return [
    '<div class="auth-brand-intro" aria-hidden="true"><img src="/naysor-identity.svg" alt=""><span>NAYSOR</span><small>PROJECT MANAGER</small></div>',
    '<div class="auth-shell">',
    '<aside class="auth-story" aria-hidden="true">',
    '<div class="auth-story-brand"><img src="/naysor-identity.svg" alt=""><span><strong>NAYSOR</strong><small>PROJECT MANAGER</small></span></div>',
    '<div class="auth-story-copy"><span>PROJECT DELIVERY SYSTEM</span><h2>Build with discipline.<br>Scale with confidence.</h2><p>One controlled workspace for engineering, schedule, cost and stage-gate decisions.</p></div>',
    '<div class="auth-story-flow"><span>ENGINEERING</span><i></i><span>SCHEDULE</span><i></i><span>GATE</span></div>',
    '</aside>',
    '<section class="auth-card">',
    '<div class="auth-brand"><span><img src="/naysor-identity.svg" alt="NAYSOR"></span><div><strong>NAYSOR</strong><small>Project Manager</small></div></div>',
    content,
    '<footer>AUTHORIZED NAYSOR USERS · SECURE PROJECT WORKSPACE</footer>',
    '</section>',
    '</div>'
  ].join("");
}

function showMessage(root, message, tone = "info") {
  const element = root.querySelector("[data-auth-message]");
  if (!element) return;
  element.className = "auth-message " + tone;
  element.textContent = message;
  element.hidden = false;
}

function passwordSetupType() {
  const match = window.location.href.match(/(?:[?#&])type=(invite|recovery)(?:[&#]|$)/);
  return match?.[1] || "";
}

function isPasswordSetupUrl() {
  return Boolean(passwordSetupType());
}

async function completePasswordSetup(client, root, session) {
  const isInvite = passwordSetupType() === "invite";
  root.hidden = false;
  root.innerHTML = authFrame([
    `<span class="auth-kicker">${isInvite ? "INVITATION ACCEPTANCE" : "PASSWORD RECOVERY"}</span>`,
    `<h1>${isInvite ? "Activate your NAYSOR account" : "Set a new password"}</h1>`,
    `<p>${isInvite ? "Accept the invitation and choose the password you will use for NAYSOR Project Manager." : "Choose a new password to continue to your secure project workspace."}</p>`,
    '<form class="auth-form" data-auth-form="recovery">',
    '<label>New password<input name="password" type="password" autocomplete="new-password" minlength="10" required></label>',
    '<label>Confirm new password<input name="confirmation" type="password" autocomplete="new-password" minlength="10" required></label>',
    '<div class="auth-message" data-auth-message hidden></div>',
    `<button class="button auth-primary" type="submit">${isInvite ? "Activate account" : "Update password"}</button>`,
    "</form>"
  ].join(""));
  return new Promise((resolve) => {
    const form = root.querySelector('[data-auth-form="recovery"]');
    form.addEventListener("submit", async event => {
      event.preventDefault();
      const values = Object.fromEntries(new FormData(form).entries());
      if (values.password !== values.confirmation) {
        showMessage(root, "The password confirmation does not match.", "error");
        return;
      }
      const button = form.querySelector('[type="submit"]');
      button.disabled = true;
      const { error } = await client.auth.updateUser({ password: values.password });
      button.disabled = false;
      if (error) {
        showMessage(root, error.message, "error");
        return;
      }
      history.replaceState({}, document.title, window.location.pathname);
      root.hidden = true;
      resolve(session);
    });
  });
}

export async function requireSession(client, config) {
  const root = document.getElementById("authRoot");
  const { data: { session } } = await client.auth.getSession();
  if (session) return isPasswordSetupUrl() ? completePasswordSetup(client, root, session) : session;

  root.hidden = false;
  root.innerHTML = authFrame([
    '<span class="auth-kicker">SECURE PROJECT WORKSPACE</span>',
    "<h1>NAYSOR Project Manager</h1>",
    "<p>Sign in to coordinate engineering, schedule, cost and stage-gate decisions in one controlled workspace.</p>",
    '<form class="auth-form" data-auth-form="login">',
    '<label>Email address<input name="email" type="email" autocomplete="username" required></label>',
    '<label>Password<input name="password" type="password" autocomplete="current-password" required></label>',
    '<div class="auth-message" data-auth-message hidden></div>',
    '<button class="button auth-primary" type="submit">Sign in</button>',
    '<button class="auth-link" type="button" data-auth-action="reset">Forgot your password?</button>',
    "</form>"
  ].join(""));

  return new Promise((resolve, reject) => {
    const form = root.querySelector("[data-auth-form=login]");
    form.addEventListener("submit", async event => {
      event.preventDefault();
      const submit = form.querySelector("[type=submit]");
      const values = Object.fromEntries(new FormData(form).entries());
      submit.disabled = true;
      submit.textContent = "Signing in...";
      const { data, error } = await client.auth.signInWithPassword({
        email: values.email,
        password: values.password
      });
      submit.disabled = false;
      submit.textContent = "Sign in";
      if (error) {
        showMessage(root, "Sign-in failed. Check your account status, email address and password.", "error");
        return;
      }
      root.hidden = true;
      resolve(data.session);
    });

    root.querySelector("[data-auth-action=reset]").addEventListener("click", async event => {
      const email = form.elements.email.value.trim();
      if (!email) {
        showMessage(root, "Enter the email address that should receive the password reset link.", "warning");
        return;
      }
      const button = event.currentTarget;
      button.disabled = true;
      const { error } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: config.publicSiteUrl
      });
      if (error) {
        const rateLimited = /rate limit/i.test(error.message || "");
        showMessage(root, rateLimited
          ? "메일 발송 한도를 초과했습니다. 관리자에게 사용자 목록의 '설정 링크' 생성을 요청하거나 잠시 후 다시 시도하세요."
          : error.message, "error");
        if (rateLimited) window.setTimeout(() => { button.disabled = false; }, 60000);
        else button.disabled = false;
        return;
      }
      showMessage(root, "Password reset email sent. Check your inbox and spam folder.", "success");
      window.setTimeout(() => { button.disabled = false; }, 60000);
    });

    const { data: listener } = client.auth.onAuthStateChange((event, nextSession) => {
      if (event === "PASSWORD_RECOVERY" && nextSession) {
        listener.subscription.unsubscribe();
        completePasswordSetup(client, root, nextSession).then(resolve);
      } else if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && nextSession) {
        listener.subscription.unsubscribe();
        if (isPasswordSetupUrl()) completePasswordSetup(client, root, nextSession).then(resolve);
        else {
          root.hidden = true;
          resolve(nextSession);
        }
      }
      if (event === "USER_DELETED") reject(new Error("User account is no longer available."));
    });
  });
}

export async function loadUserContext(client, session) {
  const [{ data: profile, error: profileError }, { data: memberships, error: membershipError }] = await Promise.all([
    client.from("profiles").select("*").eq("id", session.user.id).maybeSingle(),
    client.from("workspace_members").select("workspace_id,system_role,active").eq("profile_id", session.user.id)
  ]);
  if (profileError) throw profileError;
  if (membershipError) throw membershipError;
  const activeMembership = (memberships || []).find(item => item.active);
  return {
    profile: profile ? {...profile, email: session.user.email} : {
      id: session.user.id,
      email: session.user.email,
      full_name: session.user.user_metadata?.full_name || session.user.email,
      active: true
    },
    membership: activeMembership || null
  };
}

export async function requireWorkspaceMembership(client, context) {
  if (context.membership) return context;
  const root = document.getElementById("authRoot");
  root.hidden = false;
  root.innerHTML = authFrame([
    '<span class="auth-kicker">ACCESS SETUP</span>',
    "<h1>Workspace 배정 필요</h1>",
    "<p>" + escapeHtml(context.profile.email) + " 계정은 인증됐지만 활성 Workspace 역할이 없습니다.</p>",
    '<div class="auth-message warning" data-auth-message>최초 설치라면 첫 관리자로 초기화할 수 있습니다. 기존 운영환경이라면 System Admin에게 배정을 요청하세요.</div>',
    '<button class="button auth-primary" type="button" data-auth-action="bootstrap">최초 관리자 초기화</button>',
    '<button class="auth-link" type="button" data-auth-action="signout">로그아웃</button>'
  ].join(""));

  return new Promise(resolve => {
    root.querySelector("[data-auth-action=bootstrap]").addEventListener("click", async event => {
      event.currentTarget.disabled = true;
      const { data, error } = await client.rpc("bootstrap_first_admin", { p_workspace_slug: "naysor" });
      if (error) {
        showMessage(root, error.message, "error");
        event.currentTarget.disabled = false;
        return;
      }
      root.hidden = true;
      resolve({
        ...context,
        membership: {
          workspace_id: data.workspace_id,
          system_role: "system_admin",
          active: true
        }
      });
    });
    root.querySelector("[data-auth-action=signout]").addEventListener("click", () => client.auth.signOut());
  });
}
